import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Switch, Tooltip } from 'antd'
import { FiMoon, FiSun } from 'react-icons/fi'
import styled, { css } from 'styled-components'

import BannerBG from '../../../asserts/images/ass-report-bg.png'
import BrandedBannerBG from '../../../asserts/images/ar-branded-banner-bg.png'
import GenericPdfDownloader from './GenericPdfDownloader'
import { loadUser } from '../../../utils/storage'
import { getRole } from '../../../utils/common'
import { isDarkDeck } from '../../../utils/constant'

const BannerContainer = styled.div`
  background: url(${BannerBG}) no-repeat;
  height: 433px;
  background-size: 100%;
  padding: 105px 200px;
  position: relative;
  margin-bottom: 20px;
  .download-pdf {
    position: absolute;
    right: 20px;
    top: 15px;
    border: 1px solid #ffffff;
  }
  .avatar {
    width: 104px;
    height: 104px;
    background: #f1f3f6;
    margin-bottom: 20px;
    border-radius: 104px;
    overflow: hidden;
    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }
  .report-title {
    color: #f8fafa;
    font-size: 56px;
    letter-spacing: 0.3px;
    line-height: 120%;
    margin-bottom: 10px;
  }
  .title {
    color: #f8fafa;
    opacity: 0.6;
    font-size: 24px;
    margin-left: 2px;
  }

  /* Light/dark deck theme switch - experts only, see the role check in the
     component below. It sits in the banner action cluster, immediately left
     of COPY CLIENT LINK / SAVE AS PDF and shares their antd primary-button
     metrics (height / padding / radius / font). Hidden while the PDF is
     generated. */
  .deck-theme-switch {
    position: absolute;
    /* Draft decks only render SAVE AS PDF (no COPY CLIENT LINK), so the
       switch sits closer to the right edge. Published decks render both
       buttons, so it shifts left to clear them. */
    right: ${(props) => (props.$published ? '318px' : '25px')};
    top: 15px;
    z-index: 2;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-align-items: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    height: 31px;
    padding: 6px 15px;
    padding-top: 6px;
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5715;
    background: #2b4b8f;
    border: 1px solid #ffffff;
    border-radius: 18px;
    box-shadow: 0 2px 0 rgba(0, 0, 0, 0.045);
    text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.12);
    .ant-switch {
      margin: 0 8px;
      background: rgba(0, 0, 0, 0.25);
      &.ant-switch-checked {
        background: rgba(0, 0, 0, 0.45);
      }
      .ant-switch-loading-icon {
        color: #fff;
      }
    }
    .icon {
      display: inline-flex;
      font-size: 14px;
      opacity: 0.65;
      &.active {
        opacity: 1;
      }
    }
  }

  ${(props) =>
    props.branded &&
    css`
      background: url(${BrandedBannerBG}) no-repeat center / cover, #0a0a0a;

      .avatar {
        background: #252525;
      }

      /* COPY CLIENT LINK / SAVE AS PDF banner action buttons; the theme
         switch travels with them as part of the same action cluster. */
      .download-pdf {
        background: transparent;
        &:hover,
        &:focus {
          border-color: #ffffff !important;
          color: #ffffff !important;
        }
      }

      .deck-theme-switch {
        background: transparent;
        border-color: #c5beb3;
        color: #c5beb3;
        box-shadow: none;
        text-shadow: none;
        .ant-switch {
          background: rgba(197, 190, 179, 0.35);
          &.ant-switch-checked {
            background: #c5beb3;
          }
          .ant-switch-loading-icon {
            color: #0a0a0a;
          }
        }
      }

      .report-title {
        font-family: "Gilroy", sans-serif;
        letter-spacing: -0.3px;
      }

      .title {
        color: #a3a3a3;
        opacity: 1;
        font-family: "Gilroy", sans-serif;
      }
    `}
`

export default function Banner(props) {
  const { person, period, showPDF, id, isPublished, reportId, onThemeChange, themeChanging } = props
  const isAdmin = loadUser().role === 16
  const branded = isDarkDeck()
  const canPickTheme =
    !!onThemeChange &&
    ['Expert', 'InternalAdmin', 'partner_expert'].includes(getRole())

  const [pendingTheme, setPendingTheme] = useState(null)
  const shownBranded = pendingTheme !== null ? pendingTheme === 'dark' : branded
  useEffect(() => {
    if (pendingTheme !== null && (pendingTheme === 'dark') === branded) {
      setPendingTheme(null)
    }
  }, [branded, pendingTheme])

  const queryParams = new URLSearchParams(window.location.search)
  const uploadRequest = queryParams.get('savePDF') === 'true'
  
  return (
    <section id={id} style={{ margin: 0, padding: 0 }}>
      <BannerContainer branded={branded} $published={isPublished}>
        {canPickTheme && (
          <div className="deck-theme-switch" id="deckThemeSwitch">
            <FiSun className={`icon${shownBranded ? '' : ' active'}`} />
            <Tooltip
              title={
                shownBranded
                  ? 'Show this deck in the light theme'
                  : 'Show this deck in the dark theme'
              }
            >
              <Switch
                checked={shownBranded}
                loading={themeChanging || pendingTheme !== null}
                onChange={(checked) => {
                  const theme = checked ? 'dark' : 'light'
                  setPendingTheme(theme)
                  onThemeChange(theme)
                }}
              />
            </Tooltip>
            <FiMoon className={`icon${shownBranded ? ' active' : ''}`} />
          </div>
        )}
        <div className="avatar" id="avatar">
          {person.avatar && <img src={person.avatar.oss_url} alt="avatar" />}
        </div>
        <h1 className="report-title">
          {(person.profile && person.profile.nickname) || person.first_name} {person.last_name}'s <br />
          Comprehensive Report
        </h1>
        <h3 className="title" style={{ paddingTop: '5px'}}>
          Date of Assessment: {moment(period).format('MMM YYYY')}
        </h3>
        {showPDF && isAdmin && (
          <GenericPdfDownloader
            uploadRequest={uploadRequest}
            ids={'PDFAssessment'}
            className="download-pdf"
            person={person}
            filename={`${(person.profile && person.profile.nickname) ||
              person.first_name}-report.pdf`}
            isPublished={isPublished}
            reportId={reportId}
          />
        )}
      </BannerContainer>
    </section>
  )
}
